import { normalizeId } from './data';

/** How many placings to render: a positive count, or the whole field. */
export type RowLimit = number | 'all';

/** The podium, when an embed does not ask for anything else. */
const DEFAULT_ROW_LIMIT = 3;

/**
 * `?limit=` accepts a positive whole number or `all`.
 *
 * Anything else — zero, a negative, junk, a decimal — falls back to the default
 * rather than being coerced. `limit=0` would render a table with no rows and
 * `limit=abc` would silently become something arbitrary; in an embed nobody is
 * watching, showing the usual three is the more useful failure.
 */
export function parseRowLimit(raw: string | null): RowLimit {
  if (raw == null) return DEFAULT_ROW_LIMIT;

  const value = raw.trim().toLowerCase();
  if (value === 'all') return 'all';
  if (!/^\d+$/.test(value)) return DEFAULT_ROW_LIMIT;

  const parsed = Number.parseInt(value, 10);
  return parsed > 0 ? parsed : DEFAULT_ROW_LIMIT;
}

export interface WidgetParams {
  showId: string | null;
  classId: string | null;
  limit: RowLimit;
  /** Raw `?date=`: `YYYY-MM-DD` or `today`. Its presence turns on the day view. */
  date: string | null;
  /** Raw `?ring=`, matched against the class ring name. */
  ring: string | null;
}

function readText(params: URLSearchParams, name: string): string | null {
  const value = params.get(name)?.trim();
  return value ? value : null;
}

export function readParams(search: string): WidgetParams {
  const params = new URLSearchParams(search);
  return {
    showId: normalizeId(params.get('show')),
    classId: normalizeId(params.get('class')),
    limit: parseRowLimit(params.get('limit')),
    date: readText(params, 'date'),
    ring: readText(params, 'ring'),
  };
}

/**
 * Which widget an embed gets. A named class always wins, being the most specific
 * request; otherwise a date asks for the ring's day, and a bare show keeps the
 * original behaviour of resolving one class itself.
 */
export function widgetMode(params: WidgetParams): 'class' | 'day' {
  if (params.classId) return 'class';
  return params.date !== null ? 'day' : 'class';
}

/** Applies `?limit=` to rows that arrive already ordered by placing. */
export function sliceRows<T>(rows: T[], limit: RowLimit): T[] {
  return limit === 'all' ? rows : rows.slice(0, limit);
}
